import { useCallback, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import type { SupabaseClient } from "@supabase/supabase-js";

import { ShellChromeContext, type ShellChromeContextValue } from "../components/shell/shell-chrome";
import { useMediaQuery } from "../lib/use-media-query";
import { stageChromeForPath } from "./route-templates";

import { useAuth } from "../auth/AuthProvider";
import {
  fetchMyAccounts,
  formatAccountRoleLabel,
  formatWorkspacePickerLabel,
  persistSelectedAccount,
  readSelectedAccount,
  resolveEffectiveAccount,
  type MyAccount,
} from "../data/accounts";
import { useReadModel } from "../data/useReadModel";
import type { LiveConfig } from "../lib/live-config";
import { currentTheme, setTheme } from "../theme/theme";
import { BrandLockup } from "../components/BrandLockup";
import { BuildStamp } from "../status/BuildStamp";
import { NativeShellAbout } from "../status/NativeShellAbout";
import { SupportSessionBanner } from "../support/SupportSessionBanner";
import { useSupportSession } from "../support/SupportSessionProvider";
import { NAV_ITEMS, visibleNavItems } from "./nav-model";
import { NavCollapseIcon, NavIcon } from "./nav-icons";
import { readSidenavCollapsed, writeSidenavCollapsed } from "../lib/sidenav-preferences";
import { usePermissions } from "./usePermissions";
import { JumpPalette } from "./JumpPalette";
import { TopbarFeedbackMenu } from "./TopbarFeedbackMenu";

/**
 * Context handed to routed pages via react-router's <Outlet> so surfaces get the client +
 * config + the shell's capability check (one current_account_permissions call, shared by nav
 * and pages; fails closed to "no").
 *
 * `isSuperadmin` comes from the same call's `source` column, so it is already suppressed while a
 * support session is open — surfaces that gate a platform-only affordance on it get the honest
 * answer for free, with no second lookup and no client-side role guess.
 */
export interface ShellOutletContext {
  client: SupabaseClient;
  config: LiveConfig;
  /** Active account membership role. Null while unknown so exact role gates fail closed. */
  accountRole: string | null;
  can: (permissionKey: string) => boolean;
  isSuperadmin: boolean;
  /**
   * Re-read this caller's capability keys.
   *
   * PRD-001AE AE3: editing the role permission matrix can change what the EDITOR themselves may
   * do, and the nav is gated on those keys. Without this the shell would keep showing an item the
   * server has just started refusing, or hide one it has just granted, until a reload. Call it
   * after a successful matrix write.
   */
  refreshPermissions: () => void;
}

/**
 * The app chrome: top bar + permission-gated side nav + routed content (<Outlet/>).
 * Responsive — the side nav becomes an off-canvas drawer on narrow screens (how supervisors
 * get the console on their phone, per the brief). Rendered as a react-router layout route.
 */
export function AppShell({ client, config }: { client: SupabaseClient; config: LiveConfig }) {
  const { user, signOut } = useAuth();
  // Workspace switcher (CONSOLE-ACCOUNT-SWITCHER-LIVE): my_accounts lists the caller's
  // active memberships; failures degrade to an empty list so the config account silently
  // remains the pin. Selection persists locally and stale selections drop to the default.
  // Support session (act-as). While one is open the shell PINS the workspace to the target
  // account and hides the switcher: the actor's platform elevation is suppressed server-side, so
  // my_accounts may no longer list that account at all, and letting them wander to a workspace
  // their effective identity cannot read would only produce empty screens.
  const { session: supportSession } = useSupportSession();
  const supportAccountId = supportSession?.targetAccountId ?? null;
  const supportSessionId = supportSession?.sessionId ?? null;

  const myAccounts = useReadModel<MyAccount[]>(
    () => fetchMyAccounts(client).catch(() => []),
    [client, supportSessionId],
    0,
  );
  const accounts = myAccounts.data ?? [];
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(() => readSelectedAccount());
  const effectiveAccountId =
    supportAccountId ?? resolveEffectiveAccount(config.accountId, accounts, selectedAccountId);
  const activeAccount = accounts.find((account) => account.accountId === effectiveAccountId);
  const effectiveConfig = useMemo<LiveConfig>(() => {
    if (effectiveAccountId === config.accountId) {
      return config;
    }
    const selected = accounts.find((account) => account.accountId === effectiveAccountId);
    // While supporting, my_accounts may no longer list the pinned account at all, so the session's
    // own projection is the fallback name. If neither knows it, the config name stands rather than
    // the chrome inventing a workspace label.
    const supportName =
      effectiveAccountId === supportAccountId ? supportSession?.targetAccountName : null;
    return {
      ...config,
      accountId: effectiveAccountId,
      accountName: selected?.accountName ?? supportName ?? config.accountName,
    };
  }, [config, effectiveAccountId, accounts, supportAccountId, supportSession?.targetAccountName]);
  // The session id is the refresh key: starting or ending a session rewrites this caller's matrix
  // for the SAME account, so an accountId-only dependency would keep stale keys on screen. The
  // nonce rides alongside it so a matrix edit (AE3) can force the same re-read.
  const [permissionsNonce, setPermissionsNonce] = useState(0);
  const refreshPermissions = useCallback(() => setPermissionsNonce((nonce) => nonce + 1), []);
  const { can, isSuperadmin } = usePermissions(
    client,
    effectiveAccountId,
    `${supportSessionId ?? "none"}:${permissionsNonce}`,
  );
  const items = visibleNavItems(NAV_ITEMS, { can, isSuperadmin });
  const [navOpen, setNavOpen] = useState(false);
  // Desktop-only icon rail. On phone the sidenav is an off-canvas drawer, and CSS keeps it
  // full-width with labels there regardless of this flag: two collapse paradigms on one
  // surface would just be two things to learn.
  const [navCollapsed, setNavCollapsed] = useState(() => readSidenavCollapsed());
  // Shell v3 collapse order (product lock 2026-08-15, lock 3): between 861 and 1439px the
  // app rail yields to icons so the subrail and stage keep working width; labels return at
  // 1440, where the stored preference governs again. Below 861 the rail is the PRD-001AD
  // phone drawer and neither applies (drawer CSS restores full width + labels regardless).
  const railAutoSlim = useMediaQuery("(min-width: 861px) and (max-width: 1439px)");
  const railSlim = navCollapsed || railAutoSlim;
  // Subrail plumbing (lock 1): pages mount a ShellSubrail, which portals into the host
  // cell rendered between the rail and the stage. Registration is counted so a route swap
  // that overlaps two subrails for a frame cannot drop the grid column mid-swap.
  const [subrailHost, setSubrailHost] = useState<HTMLDivElement | null>(null);
  const [subrailCount, setSubrailCount] = useState(0);
  const registerSubrail = useCallback(() => {
    setSubrailCount((count) => count + 1);
    return () => setSubrailCount((count) => count - 1);
  }, []);
  const shellChrome = useMemo<ShellChromeContextValue>(
    () => ({ subrailHost, registerSubrail }),
    [subrailHost, registerSubrail],
  );
  const hasSubrail = subrailCount > 0;
  const location = useLocation();
  // Stage chrome comes from the route registry (route-templates.ts): the CAD desk keeps
  // its map-first chrome, migrated v3 routes get the zero-padding stage, and everything
  // else stays on the legacy padded scroller until its migration PR flips the registry.
  const stageChrome = stageChromeForPath(location.pathname);
  const feedbackRoleLabel =
    supportSession !== null
      ? formatAccountRoleLabel(supportSession.targetRole)
      : activeAccount !== undefined
        ? formatAccountRoleLabel(activeAccount.role)
        : "Signed in";
  const feedbackPageLabel = feedbackPageLabelForPath(location.pathname);
  const accountRole = supportSession?.targetRole ?? activeAccount?.role ?? null;

  return (
    <div className="appshell">
      {supportSession !== null ? (
        <SupportSessionBanner
          session={supportSession}
          timeZone={effectiveConfig.displayTimeZone}
          accountNameHint={activeAccount?.accountName ?? null}
        />
      ) : null}
      <header className="topbar">
        <button
          className="topbar__menu"
          type="button"
          aria-label="Toggle navigation"
          aria-expanded={navOpen}
          onClick={() => setNavOpen((open) => !open)}
        >
          ☰
        </button>
        <BrandLockup className="topbar__brand brandlockup" />
        {supportSession !== null ? (
          // Pinned, not chosen: the workspace is whatever account the support session targets.
          <span className="topbar__account">
            {effectiveConfig.accountName}
            <span className="topbar__account-role">
              {formatAccountRoleLabel(supportSession.targetRole)}
            </span>
          </span>
        ) : accounts.length > 1 ? (
          <label className="topbar__workspace">
            <span className="topbar__workspace-label">Workspace</span>
            <select
              aria-label="Workspace"
              value={effectiveAccountId}
              onChange={(event) => {
                setSelectedAccountId(event.target.value);
                persistSelectedAccount(event.target.value);
              }}
            >
              {accounts.map((account) => (
                <option key={account.accountId} value={account.accountId}>
                  {formatWorkspacePickerLabel(account, accounts)}
                </option>
              ))}
            </select>
            {activeAccount ? (
              <span className="topbar__workspace-role">{formatAccountRoleLabel(activeAccount.role)}</span>
            ) : null}
          </label>
        ) : (
          <span className="topbar__account">
            {effectiveConfig.accountName}
            {activeAccount ? (
              <span className="topbar__account-role">{formatAccountRoleLabel(activeAccount.role)}</span>
            ) : null}
          </span>
        )}
        <span className="topbar__spacer" />
        <JumpPalette items={items} client={client} accountId={effectiveAccountId} />
        <BuildStamp surface="console" />
        <ThemeToggle />
        <span className="topbar__user">{user?.email ?? "supervisor"}</span>
        <TopbarFeedbackMenu
          client={client}
          accountId={effectiveAccountId}
          accountName={effectiveConfig.accountName}
          roleLabel={feedbackRoleLabel}
          pageLabel={feedbackPageLabel}
        />
        <button className="topbar__signout" type="button" onClick={() => void signOut()}>
          Sign out
        </button>
      </header>

      <div
        className={`appshell__body${railSlim ? " appshell__body--slim" : ""}${
          hasSubrail ? " appshell__body--sub" : ""
        }`}
      >
        {navOpen ? (
          <button
            type="button"
            className="sidenav-backdrop"
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
          />
        ) : null}
        <nav
          className={`sidenav${navOpen ? " sidenav--open" : ""}${
            railSlim ? " sidenav--collapsed" : ""
          }`}
          aria-label="Primary"
          data-collapsed={railSlim ? "true" : "false"}
        >
          <div className="sidenav__head">
            <span className="sidenav__eyebrow">Workspace</span>
            <span className="sidenav__account" title={effectiveConfig.accountName}>
              {effectiveConfig.accountName}
            </span>
          </div>
          <p className="sidenav__section-label">Dispatch</p>
          {items.map((item) => (
            <NavLink
              key={item.key}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                isActive ? "sidenav__link sidenav__link--active" : "sidenav__link"
              }
              onClick={() => setNavOpen(false)}
              // Collapsed (by preference OR the 861-1439 auto-slim band), the glyph is all
              // a sighted user gets, so the name has to come from somewhere. aria-label
              // matches the label text exactly, so the two can never disagree; data-tip
              // drives a CSS tooltip on hover AND keyboard focus.
              aria-label={railSlim ? item.label : undefined}
              data-tip={railSlim ? item.label : undefined}
            >
              <NavIcon icon={item.icon} />
              <span className="sidenav__label">{item.label}</span>
            </NavLink>
          ))}
          <button
            type="button"
            className="sidenav__collapse"
            onClick={() => {
              const next = !navCollapsed;
              setNavCollapsed(next);
              writeSidenavCollapsed(next);
            }}
            aria-expanded={!navCollapsed}
            aria-label={navCollapsed ? "Expand navigation" : "Collapse navigation"}
            data-tip={navCollapsed ? "Expand navigation" : undefined}
          >
            <NavCollapseIcon />
            <span className="sidenav__label">Collapse</span>
          </button>
          <div className="sidenav__mobile-actions">
            <button className="sidenav__signout" type="button" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
          <div className="sidenav__build">
            <BuildStamp surface="console" compact />
            <NativeShellAbout />
          </div>
        </nav>

        {/* Subrail host cell. Hidden (no box) until a page registers a ShellSubrail, so
            routes without one never pay the grid column. The hidden attribute needs the
            matching CSS guard because the class sets display: flex. */}
        <div className="appshell__subrail" hidden={!hasSubrail} ref={setSubrailHost} />

        <main
          className={
            stageChrome === "cad"
              ? "appshell__content appshell__content--cad"
              : stageChrome === "stage"
                ? "appshell__content appshell__content--stage"
                : "appshell__content"
          }
        >
          <ShellChromeContext.Provider value={shellChrome}>
            <Outlet
              context={
                {
                  accountRole,
                  client,
                  config: effectiveConfig,
                  can,
                  isSuperadmin,
                  refreshPermissions,
                } satisfies ShellOutletContext
              }
            />
          </ShellChromeContext.Provider>
        </main>
      </div>
    </div>
  );
}

function feedbackPageLabelForPath(pathname: string): string {
  const normalized = pathname === "" ? "/" : pathname;
  const exact = NAV_ITEMS.find((item) => item.path === normalized);
  if (exact !== undefined) {
    return `Console · ${exact.label}`;
  }
  const nested = NAV_ITEMS.find(
    (item) => item.path !== "/" && normalized.startsWith(`${item.path}/`),
  );
  if (nested !== undefined) {
    return `Console · ${nested.label}`;
  }
  return `Console · ${normalized}`;
}

function ThemeToggle() {
  const [theme, setLocalTheme] = useState(() => currentTheme());
  function toggle() {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    setLocalTheme(next);
  }
  return (
    <button
      className="topbar__theme"
      type="button"
      onClick={toggle}
      aria-label={`Switch to ${theme === "light" ? "dark" : "light"} theme`}
    >
      {theme === "light" ? "☾" : "☀"}
    </button>
  );
}
